import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import useNotification from '../../../atoms/Notification/useNotification';
import Search from '../../../atoms/Icon/Search';
import Bookmark from '../../../atoms/Icon/Bookmark';
import { AgenticSearchInputProps, DEFAULT_INPUT_PLACEHOLDER, LookupItem } from '../AskAsperAgentic.types';
import Send from '../../../atoms/Icon/Send';
import QuestionCircleOutlined from '../../../atoms/Icon/QuestionCircleOutlined';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import { useSlashLookup } from './useSlashLookup';
import LookupSuggestionDropdown from './LookupSuggestionDropdown';
import LookupCategoryDropdown from './LookupCategoryDropdown';
import LookupHighlightLayer from './LookupHighlightLayer';
import { useLookupInputRanges } from './useLookupInputRanges';
import { createMixpanelEventFirer } from '../utils/mixpanelEvents';
import {
    fireEntityFilterItemDeselect,
    fireEntityFilterItemSelect,
    fireSlashCommandOptionSelect,
    fireTimePeriodSelect,
    isTimePeriodLookupItem,
} from '../utils/slashCommandMixpanel';

/**
 * AgenticSearchInput — Search input box with Question Bank and Save as Custom buttons.
 *
 * When `lookupItems` is provided, typing `/` (at start of input or after
 * whitespace) opens a slash-command picker. The user picks a category, then a
 * suggestion; ArrowUp/Down navigate, Enter/Tab confirms, Esc dismisses. A
 * confirmed pick *removes* the typed `/query` and inserts just the entity
 * name (no slash visible). Selection state is tracked positionally via
 * `useLookupInputRanges` so the highlight pill, atomic Backspace, and the
 * host's `onLookupsChange` mirror stay aligned with the textarea content.
 */

/** Typography + box-model classes shared between the textarea and the
 *  `LookupHighlightLayer` mirror. Must stay identical so highlight pills line
 *  up exactly with the underlying glyphs. */
const LOOKUP_INPUT_TYPOGRAPHY =
    "font-['Open_Sans'] font-normal text-[16px] leading-6 m-0 p-0";
const AgenticSearchInput: React.FC<AgenticSearchInputProps> = ({
    value: controlledValue,
    onChange,
    onSubmit,
    onQuestionBankClick,
    onSaveAsCustom,
    onFocus,
    placeholder = DEFAULT_INPUT_PLACEHOLDER,
    isLoading = false,
    disabled = false,
    lookupItems,
    selectedLookups,
    onLookupsChange,
    onLoadLookups,
    lookupCategories,
    onTrackEvent,
    analyticsThreadId,
    analyticsSessionId,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const { success } = useNotification();
    const [internalValue, setInternalValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [caretPos, setCaretPos] = useState(0);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    /** When set, restore caret to this position after the next render. */
    const pendingCaretRef = useRef<number | null>(null);
    const hasLoadedLookupsRef = useRef(false);
    const selectedLookupsRef = useRef(selectedLookups);
    selectedLookupsRef.current = selectedLookups;

    const fireEvent = useMemo(() => createMixpanelEventFirer(onTrackEvent), [onTrackEvent]);
    const analyticsContext = useMemo(
        () => ({ threadId: analyticsThreadId, sessionId: analyticsSessionId }),
        [analyticsThreadId, analyticsSessionId],
    );

    const handleLookupRemoved = useCallback(
        (item: LookupItem) => {
            fireEntityFilterItemDeselect(fireEvent, item, analyticsContext);
        },
        [fireEvent, analyticsContext],
    );

    const inputValue = controlledValue !== undefined ? controlledValue : internalValue;
    const hasValue = inputValue && inputValue.trim().length > 0;
    const canSubmit = hasValue && !isLoading && !disabled;

    const slash = useSlashLookup(inputValue, caretPos, lookupItems, {
        categories: lookupCategories,
    });

    const lookupRanges = useLookupInputRanges({
        inputValue,
        selectedLookups,
        onLookupsChange,
        onLookupRemoved: handleLookupRemoved,
    });

    // Apply pending caret position (used after `accept` replaces the slash token).
    useLayoutEffect(() => {
        if (pendingCaretRef.current !== null && textareaRef.current) {
            const pos = pendingCaretRef.current;
            textareaRef.current.setSelectionRange(pos, pos);
            setCaretPos(pos);
            pendingCaretRef.current = null;
        }
    });

    /** Sync caret position from the textarea after user-driven cursor changes. */
    const syncCaretFromTextarea = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        setCaretPos(el.selectionStart ?? 0);
    }, []);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value;
            // Rebase existing entity ranges against the user edit BEFORE the
            // value prop flips, so range positions stay consistent with the
            // string they describe.
            lookupRanges.onEdit(inputValue, newValue);
            if (onChange) {
                onChange(newValue);
            } else {
                setInternalValue(newValue);
            }
            setCaretPos(e.target.selectionStart ?? newValue.length);
        },
        [inputValue, lookupRanges, onChange],
    );

    const handleSubmit = useCallback(() => {
        if (canSubmit && onSubmit) {
            onSubmit(inputValue);
        }
    }, [canSubmit, onSubmit, inputValue]);

    const acceptSuggestion = useCallback(
        (index: number) => {
            const match = slash.suggestions[index];
            if (!match) return;
            const result = slash.accept(match.item);
            if (!result) return;
            const { nextValue, nextCaret, item, range } = result;
            lookupRanges.commitPick(inputValue, nextValue, {
                lookup: item,
                start: range.start,
                end: range.end,
            });
            if (onChange) {
                onChange(nextValue);
            } else {
                setInternalValue(nextValue);
            }
            pendingCaretRef.current = nextCaret;

            const prev = selectedLookupsRef.current ?? [];
            const exists = prev.some(
                (l) => l.column_name === item.column_name && l.entity_name === item.entity_name,
            );
            const nextSelected = exists ? prev : [...prev, item];
            if (isTimePeriodLookupItem(item)) {
                fireTimePeriodSelect(fireEvent, item, analyticsContext);
            } else {
                fireEntityFilterItemSelect(fireEvent, nextSelected, analyticsContext);
            }
        },
        [slash, inputValue, lookupRanges, onChange, fireEvent, analyticsContext],
    );

    const pickCategoryByIndex = useCallback(
        (index: number) => {
            const cat = slash.categories[index];
            if (!cat) return;
            slash.pickCategory(cat.id);
            fireSlashCommandOptionSelect(fireEvent, cat.id, cat.label, analyticsContext);
        },
        [slash, fireEvent, analyticsContext],
    );

    const deleteLookupTokenAtCaret = useCallback((): boolean => {
        const el = textareaRef.current;
        if (!el) return false;
        if (el.selectionStart !== el.selectionEnd) return false;
        const caret = el.selectionStart ?? 0;
        const range = lookupRanges.findRangeAtCaret(caret);
        if (!range) return false;
        const nextValue = inputValue.slice(0, range.start) + inputValue.slice(range.end);
        // The deletion edit overlaps the entity range — rebase will drop the
        // range from state and emit a `onLookupsChange` update for the host.
        lookupRanges.onEdit(inputValue, nextValue);
        if (onChange) {
            onChange(nextValue);
        } else {
            setInternalValue(nextValue);
        }
        pendingCaretRef.current = range.start;
        return true;
    }, [inputValue, lookupRanges, onChange]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (slash.isActive) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    slash.setHighlightIndex(slash.highlightIndex + 1);
                    return;
                }
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    slash.setHighlightIndex(slash.highlightIndex - 1);
                    return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    if (slash.mode === 'category') {
                        pickCategoryByIndex(slash.highlightIndex);
                    } else {
                        acceptSuggestion(slash.highlightIndex);
                    }
                    return;
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    slash.close();
                    return;
                }
            }

            // Atomic delete: when the caret sits at the right edge of a
            // confirmed entity range, a single Backspace nukes the entire
            // entity in one stroke (and drops it from `selectedLookups`).
            if (e.key === 'Backspace' && !e.metaKey && !e.ctrlKey && !e.altKey) {
                if (deleteLookupTokenAtCaret()) {
                    e.preventDefault();
                    return;
                }
            }

            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [slash, acceptSuggestion, handleSubmit, pickCategoryByIndex, deleteLookupTokenAtCaret],
    );

    const handleSaveAsCustom = useCallback(() => {
        if (hasValue && onSaveAsCustom) {
            onSaveAsCustom(inputValue);
            success({
                message: 'Question saved',
                description: 'Your custom question has been saved successfully.',
                placement: 'bottomRight',
            });
        }
    }, [hasValue, onSaveAsCustom, inputValue, success]);

    const handleFocus = useCallback(() => {
        setIsFocused(true);
        if (!hasLoadedLookupsRef.current) {
            hasLoadedLookupsRef.current = true;
            onLoadLookups?.();
        }
        onFocus?.();
    }, [onFocus, onLoadLookups]);

    useEffect(() => {
        // Keep caretPos within bounds when value is externally reset (e.g. parent clears on send).
        if (caretPos > inputValue.length) {
            setCaretPos(inputValue.length);
        }
    }, [inputValue, caretPos]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className={`relative bg-white border border-white rounded-[15px] flex flex-col h-[220px] justify-between p-5 w-full max-w-[764px] ${
                    isFocused
                        ? 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.7)]'
                        : hasValue
                          ? 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.5)]'
                          : 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.32)]'
                }`}
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-search-input
            >
                {slash.isActive && slash.mode === 'category' ? (
                    <LookupCategoryDropdown
                        categories={slash.categories}
                        highlightIndex={slash.highlightIndex}
                        onHighlight={slash.setHighlightIndex}
                        onSelect={pickCategoryByIndex}
                        data-test-id={dataTestId ? `${dataTestId}-lookup-category-dropdown` : undefined}
                    />
                ) : (
                    <LookupSuggestionDropdown
                        suggestions={slash.isActive ? slash.suggestions : []}
                        highlightIndex={slash.highlightIndex}
                        onHighlight={slash.setHighlightIndex}
                        onSelect={acceptSuggestion}
                        data-test-id={dataTestId ? `${dataTestId}-lookup-dropdown` : undefined}
                    />
                )}

                {/* Search Input Area */}
                <div className="flex gap-2 items-start py-[7px] w-full">
                    <div className="shrink-0">
                        <Search size={20} color="rgba(0,0,0,0.85)" />
                    </div>
                    {/*
                     * Relative wrapper hosts both the textarea (the editable
                     * surface the user sees on top) and the LookupHighlightLayer
                     * (an invisible mirror that paints highlight pills behind
                     * confirmed entity ranges). The two must share IDENTICAL
                     * typography / box-model so highlight pills track the
                     * underlying glyphs as the text reflows.
                     */}
                    <div className="relative flex-1 min-h-[80px]">
                        <LookupHighlightLayer
                            value={inputValue}
                            ranges={lookupRanges.ranges}
                            textareaRef={textareaRef}
                            mirrorClassName={LOOKUP_INPUT_TYPOGRAPHY}
                            data-test-id={dataTestId ? `${dataTestId}-highlight-layer` : undefined}
                        />
                        <textarea
                            ref={textareaRef}
                            className={`relative w-full ${LOOKUP_INPUT_TYPOGRAPHY} text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.45)] border-0 outline-none resize-none bg-transparent`}
                            placeholder={placeholder}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onKeyUp={syncCaretFromTextarea}
                            onClick={syncCaretFromTextarea}
                            onSelect={syncCaretFromTextarea}
                            onFocus={handleFocus}
                            onBlur={() => setIsFocused(false)}
                            disabled={disabled || isLoading}
                            data-test-id={`${dataTestId}-textarea`}
                        />
                    </div>
                </div>

                {/* Bottom Actions */}
                <div className="flex items-center justify-between w-full gap-2">
                    {/* Left Actions */}
                    <div className="flex gap-2.5 items-center min-w-0">
                        {/* Question Bank Button */}
                        <Tooltip
                            title="Browse sample questions"
                            placement="top"
                            getPopupContainer={() => document.body}
                        >
                            <button
                                type="button"
                                className="bg-white border border-[#d0d5f5] rounded-lg flex gap-2 items-center h-10 px-4 cursor-pointer hover:bg-[#fafafa] transition-colors overflow-hidden relative whitespace-nowrap"
                                onClick={onQuestionBankClick}
                                data-test-id={`${dataTestId}-question-bank`}
                                aria-label="Browse sample questions"
                            >
                                <span className="shrink-0">
                                    <QuestionCircleOutlined size={16} color="#3b2c77" />
                                </span>
                                <span className="font-['Open_Sans'] font-normal text-sm leading-6 text-[#3b2c77] relative z-10 truncate">
                                    Question Bank
                                </span>
                            </button>
                        </Tooltip>

                        {/* Save as Custom Button */}
                        <Tooltip title="Save as template" placement="top" getPopupContainer={() => document.body}>
                            <span className="inline-flex">
                                <button
                                    type="button"
                                    className={`bg-white border border-[#e8eafa] rounded-lg flex gap-2 items-center h-10 px-4 transition-colors overflow-hidden relative whitespace-nowrap ${
                                        hasValue ? 'cursor-pointer hover:bg-[#fafafa]' : 'cursor-not-allowed opacity-60'
                                    }`}
                                    onClick={handleSaveAsCustom}
                                    disabled={!hasValue}
                                    data-test-id={`${dataTestId}-save-custom`}
                                    aria-label="Save as template"
                                >
                                    <span className="shrink-0">
                                        <Bookmark size={16} color={hasValue ? '#3B2C77' : 'rgba(0,0,0,0.25)'} />
                                    </span>
                                    <span
                                        className={`font-['Open_Sans'] font-normal text-sm leading-6 relative z-10 truncate ${
                                            hasValue ? 'text-[#3b2c77]' : 'text-[rgba(0,0,0,0.25)]'
                                        }`}
                                    >
                                        Save as custom
                                    </span>
                                </button>
                            </span>
                        </Tooltip>
                    </div>

                    {/* Send Button */}
                    <button
                        type="button"
                        className={`flex items-center gap-0.5 rounded px-3 py-1 pl-3 pr-2.5 transition-colors shrink-0 ${
                            canSubmit ? 'bg-[#3B2C77] cursor-pointer hover:bg-gray-800' : 'bg-black/50 cursor-not-allowed'
                        }`}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        data-test-id={`${dataTestId}-send`}
                    >
                        <span className="font-['Open_Sans'] font-normal text-base leading-6 text-[#fbfaff] pr-1">Send</span>
                        <Send size={20} color="#fbfaff" />
                    </button>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticSearchInput;

export type { LookupItem };
